// Financials tab redesign — the four party schedule tables (Buyer / Seller /
// Broker / TPS), one visible at a time behind the tab switcher. All four ride
// the single amortization read (the servicing ledger's period spine); rows are
// strictly view-only — corrections happen upstream, never by editing booked
// history. A row with a recorded payment drills into the allocation drawer.

import { StatusBadge, type StatusTone } from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import { DataTable, type Column } from "@/components/layout/DataTable";
import type {
  BrokerSchedulePeriodDto,
  BuyerSchedulePeriodDto,
  SellerSchedulePeriodDto,
  TpsSchedulePeriodDto,
} from "@/api/portfolioAdmin/amortizationApi";

// 50-row pages — a deliberate departure from the DataTable 25 default (locked
// Financials-redesign decision): schedules run to hundreds of periods and the
// heavier page keeps a year+ visible at once.
export const SCHEDULE_PAGE_SIZE = 50;

const STATUS_LABEL: Record<string, string> = {
  upcoming: "Upcoming",
  "on-time": "On time",
  late: "Late",
  partial: "Partial",
  missed: "Missed",
  nsf: "NSF",
  prepaid: "Prepaid",
};

function statusTone(status: string): StatusTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "ok";
    case "late":
    case "partial":
      return "warn";
    case "missed":
    case "nsf":
      return "danger";
    default:
      return "neutral";
  }
}

function statusBadge(status: string) {
  return <StatusBadge tone={statusTone(status)}>{STATUS_LABEL[status] ?? status}</StatusBadge>;
}

/** A period the buyer still owes (future or not fully settled). */
function isRemaining(status: string): boolean {
  return status === "upcoming" || status === "partial" || status === "missed" || status === "nsf";
}

function money(value: number): string {
  return formatCurrency(value, { maximumFractionDigits: 2 });
}

function moneyOrDash(value: number): string {
  return value > 0 ? money(value) : "—";
}

interface ScheduleTableProps<T extends { paymentId: string | null }> {
  periods: T[];
  /** Fires with the period's recorded payment id — the allocation-drawer
   *  drill-down. Rows with no recorded payment no-op. */
  onOpenAllocation: (paymentId: string) => void;
}

function byPeriodIndex<T extends { periodIndex: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.periodIndex - b.periodIndex);
}

function rowClick<T extends { paymentId: string | null }>(
  onOpenAllocation: (paymentId: string) => void,
) {
  return (row: T) => {
    if (row.paymentId) onOpenAllocation(row.paymentId);
  };
}

export function BuyerScheduleTable({ periods, onOpenAllocation }: ScheduleTableProps<BuyerSchedulePeriodDto>) {
  // BLA Core §2.4 — the fee and combined-debit columns render only for a loan
  // that carries the fee, so a grandfathered schedule looks exactly as it did.
  const hasAdminFee = periods.some((p) => p.paymentAdministrationFee > 0);
  const columns: Column<BuyerSchedulePeriodDto>[] = [
    {
      key: "dueDate",
      header: "Due date",
      render: (p) => (
        <>
          {formatCalendarDate(p.dueDate)}
          {p.isBalloon && (
            <span className="ml-space-2">
              <StatusBadge tone="info">Balloon</StatusBadge>
            </span>
          )}
          {isRemaining(p.status) && (
            <span className="ml-space-2 text-caption text-gray-mid">remaining</span>
          )}
        </>
      ),
    },
    { key: "interest", header: "Interest", className: "text-right tabular-nums", render: (p) => money(p.expectedInterest) },
    { key: "principal", header: "Principal", className: "text-right tabular-nums", render: (p) => money(p.expectedPrincipal) },
    { key: "total", header: "Total", className: "text-right tabular-nums", render: (p) => money(p.expectedTotal) },
    ...(hasAdminFee
      ? [
          {
            key: "adminFee",
            header: "Admin fee",
            className: "text-right tabular-nums",
            // The balloon carries no fee (§2.4), so its cell is an em-dash
            // rather than $0.00 — a nil that means "not applicable here".
            render: (p: BuyerSchedulePeriodDto) =>
              p.paymentAdministrationFee > 0 ? money(p.paymentAdministrationFee) : "—",
          },
          {
            key: "totalDebit",
            header: "Total debit",
            className: "text-right tabular-nums font-medium",
            render: (p: BuyerSchedulePeriodDto) => money(p.totalMonthlyDebit),
          },
        ]
      : []),
    {
      key: "balance",
      header: "Balance after",
      className: "text-right tabular-nums",
      render: (p) => (p.principalBalanceAfter !== null ? money(p.principalBalanceAfter) : "—"),
    },
    { key: "status", header: "Status", render: (p) => statusBadge(p.status) },
  ];
  return (
    <>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        The loan&apos;s level-payment schedule. Remaining periods (still owed) are
        marked; a row with a recorded payment opens its full waterfall allocation.
        {hasAdminFee && (
          <>
            {" "}
            Total debit is what the borrower is charged each month — principal and
            interest plus the payment administration fee, taken together as one
            payment.
          </>
        )}
      </p>
      <DataTable
        columns={columns}
        rows={byPeriodIndex(periods)}
        rowKey={(p) => String(p.periodIndex)}
        pagination={{ pageSize: SCHEDULE_PAGE_SIZE }}
        onRowClick={rowClick(onOpenAllocation)}
        rowTestId={(p) => `buyer-period-${p.periodIndex}`}
        emptyMessage="No schedule yet — it derives from the loan's first payment date and term."
        data-testid="buyer-schedule-table"
      />
    </>
  );
}

export function SellerScheduleTable({ periods, onOpenAllocation }: ScheduleTableProps<SellerSchedulePeriodDto>) {
  const columns: Column<SellerSchedulePeriodDto>[] = [
    { key: "dueDate", header: "Due date", render: (p) => formatCalendarDate(p.dueDate) },
    { key: "expInterest", header: "Expected interest", className: "text-right tabular-nums", render: (p) => money(p.sellerExpectedInterest) },
    { key: "expPrincipal", header: "Expected principal", className: "text-right tabular-nums", render: (p) => money(p.sellerExpectedPrincipal) },
    { key: "expTotal", header: "Expected total", className: "text-right tabular-nums", render: (p) => money(p.sellerExpectedTotal) },
    { key: "received", header: "Received", className: "text-right tabular-nums", render: (p) => moneyOrDash(p.sellerReceived) },
    {
      // The Buyer Consulting Fee carve (WF1 P10 + P11) taken out of this
      // period. Rendered UNCONDITIONALLY, like the broker table's own carve
      // column and unlike the buyer table's admin-fee pair: a column that
      // appears and disappears as rows are collected or reversed is harder to
      // read than a column of em-dashes, and this one explains a figure the
      // reader is already looking at.
      key: "consulting",
      header: "Consulting fee",
      className: "text-right tabular-nums",
      render: (p) => moneyOrDash(p.sellerConsultingDeduction ?? 0),
    },
    { key: "status", header: "Status", render: (p) => statusBadge(p.status) },
  ];
  return (
    <>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        <span className="font-semibold">Expected</span> is the note&apos;s scheduled
        interest &amp; principal accruing to the seller, always GROSS.{" "}
        <span className="font-semibold">Received</span> is the actual seller
        distribution — net of Phoenix&apos;s servicing fee/spread and of the broker
        and buyer consulting fee carves. The consulting fee taken out of each
        period is itemized in its own column; the variance between the two
        remaining columns is expected.
      </p>
      <DataTable
        columns={columns}
        rows={byPeriodIndex(periods)}
        rowKey={(p) => String(p.periodIndex)}
        pagination={{ pageSize: SCHEDULE_PAGE_SIZE }}
        onRowClick={rowClick(onOpenAllocation)}
        rowTestId={(p) => `seller-period-${p.periodIndex}`}
        emptyMessage="No seller schedule yet — it mirrors the loan's payment periods."
        data-testid="seller-schedule-table"
      />
    </>
  );
}

export function BrokerScheduleTable({ periods, onOpenAllocation }: ScheduleTableProps<BrokerSchedulePeriodDto>) {
  const columns: Column<BrokerSchedulePeriodDto>[] = [
    { key: "dueDate", header: "Due date", render: (p) => formatCalendarDate(p.dueDate) },
    { key: "paid", header: "Broker fee paid", className: "text-right tabular-nums", render: (p) => moneyOrDash(p.brokerPaid) },
    { key: "status", header: "Status", render: (p) => statusBadge(p.status) },
  ];
  return (
    <>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        Actual broker-routed distributions per period (the monthly Stream-1
        carve; co-broker legs appear when captured). The fee only flows once a
        period&apos;s payment is recorded — the first payment at closing carries
        no broker carve.
      </p>
      <DataTable
        columns={columns}
        rows={byPeriodIndex(periods)}
        rowKey={(p) => String(p.periodIndex)}
        pagination={{ pageSize: SCHEDULE_PAGE_SIZE }}
        onRowClick={rowClick(onOpenAllocation)}
        rowTestId={(p) => `broker-period-${p.periodIndex}`}
        emptyMessage="No broker schedule yet — it mirrors the loan's payment periods."
        data-testid="broker-schedule-table"
      />
    </>
  );
}

const DISTRIBUTION_LABEL: Record<string, string> = {
  Pending: "Pending",
  Paid: "Distributed",
  Reversed: "Reversed",
  Failed: "Failed",
  Voided: "Voided",
};

function distributionTone(status: string): StatusTone {
  switch (status) {
    case "Paid":
      return "ok";
    case "Pending":
      return "warn";
    case "Reversed":
    case "Failed":
      return "danger";
    default:
      return "neutral";
  }
}

export function TpsScheduleTable({ periods, onOpenAllocation }: ScheduleTableProps<TpsSchedulePeriodDto>) {
  const columns: Column<TpsSchedulePeriodDto>[] = [
    { key: "dueDate", header: "Due date", render: (p) => formatCalendarDate(p.dueDate) },
    {
      key: "feeOnTop",
      header: "Servicing fee (on top)",
      className: "text-right tabular-nums",
      render: (p) => moneyOrDash(p.servicingFeeOnTop),
    },
    {
      key: "feeCollected",
      header: "Platform fee collected",
      className: "text-right tabular-nums",
      render: (p) => moneyOrDash(p.tpsFeeCollected),
    },
    {
      key: "distribution",
      header: "Distribution",
      render: (p) =>
        p.distributionStatus ? (
          <StatusBadge tone={distributionTone(p.distributionStatus)}>
            {DISTRIBUTION_LABEL[p.distributionStatus] ?? p.distributionStatus}
          </StatusBadge>
        ) : (
          "—"
        ),
    },
    { key: "status", header: "Status", render: (p) => statusBadge(p.status) },
  ];
  return (
    <>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        The servicer&apos;s per-period platform fee (on top of the payment when
        charged to the borrower, otherwise deducted from it) and, on TPS-serviced
        loans, the period&apos;s distribution status.
      </p>
      <DataTable
        columns={columns}
        rows={byPeriodIndex(periods)}
        rowKey={(p) => String(p.periodIndex)}
        pagination={{ pageSize: SCHEDULE_PAGE_SIZE }}
        onRowClick={rowClick(onOpenAllocation)}
        rowTestId={(p) => `tps-period-${p.periodIndex}`}
        emptyMessage="No TPS schedule yet — it mirrors the loan's payment periods."
        data-testid="tps-schedule-table"
      />
    </>
  );
}
